import React, { useEffect } from "react";
import { Link } from "@/components/LocaleLink";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Check, X, CheckCircle2, Users, ClipboardList, FileText, Building2, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { getKommuner } from "@/data/kommuner";
import { getIndsatser } from "@/data/indsatser";
import { CONTACT } from "@/i18n/translations";
import { useSeo } from "@/hooks/useSeo";
import { trackEvent, EVENTS } from "@/lib/analytics";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import { CommissionerPdfButton } from "@/components/LazyCommissionerPdf";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const IMG = "https://images.unsplash.com/photo-1765366417046-f46361a7f26f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Section = ({ children, className = "", ...rest }) => (
  <section className={`max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-20 ${className}`} {...rest}>{children}</section>
);

const Kommuner = () => {
  const { lang } = useLang();
  const k = getKommuner(lang);
  const products = getIndsatser(lang).products;
  useSeo(k.seoTitle, k.seoDesc);

  useEffect(() => {
    trackEvent(EVENTS.COMMISSIONER_PAGE_VIEW, { lang });
  }, [lang]);

  const cta = (type) => () => trackEvent(EVENTS.COMMISSIONER_CTA_CLICK, { cta: type, lang });

  return (
    <>
      {/* HERO */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden" data-testid="kommuner-hero">
        <div className="absolute inset-0 -z-10">
          <img src={IMG} alt="" className="h-full w-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0 bg-[#122019]/85" />
        </div>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#8EA694] border border-[#8EA694]/40 rounded-full px-4 py-1.5">
              {k.heroBadge}
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">{k.heroTitle}</h1>
            <p className="mt-6 text-lg text-[#EAE5D9] leading-relaxed max-w-2xl">{k.heroSub}</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/kontakt?kilde=kommune-side" onClick={cta("hero-primary")} className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#F7F5F0] text-[#1A2E24] px-8 py-4 font-medium hover:bg-white transition-colors duration-300" data-testid="kommuner-hero-primary">
                {k.heroPrimary} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/find-forloeb" onClick={() => { cta("hero-finder")(); trackEvent(EVENTS.COMMISSIONER_FINDER_CLICK, { lang }); }} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white px-8 py-4 font-medium hover:bg-white/10 transition-colors duration-300" data-testid="kommuner-hero-secondary">
                {k.heroSecondary}
              </Link>
            </div>
            <div className="mt-8">
              <div className="[&>a]:!bg-white/10 [&>a]:!text-white [&>a:hover]:!bg-white/20 inline-block">
                <CommissionerPdfButton onDownload={() => trackEvent(EVENTS.COMMISSIONER_PDF_DOWNLOAD, { lang })} />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SITUATIONS */}
      <Section data-testid="kommuner-situations">
        <Reveal><Eyebrow>{k.situationsTitle}</Eyebrow></Reveal>
        <Reveal delay={0.05}><p className="mt-5 font-display text-2xl sm:text-3xl text-[#1A2E24] leading-snug max-w-3xl">{k.situationsIntro}</p></Reveal>
        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          {k.situations.map((sit) => (
            <div key={sit} className="flex items-start gap-3 rounded-2xl bg-white border border-black/5 px-5 py-4 text-[#3B5245]">
              <CheckCircle2 className="h-5 w-5 text-[#8EA694] shrink-0 mt-0.5" /> {sit}
            </div>
          ))}
        </div>
        <div className="mt-6 rounded-2xl bg-[#EAE5D9] p-6 border-l-4 border-[#C47C5D] text-[#3B5245] leading-relaxed">{k.situationsNote}</div>
      </Section>

      {/* PROCESS */}
      <section className="bg-[#1A2E24] text-white" data-testid="kommuner-process">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <Reveal><Eyebrow dark>{k.processTitle}</Eyebrow></Reveal>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {k.processSteps.map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: (i % 3) * 0.07 }} className="rounded-2xl bg-white/[0.04] border border-white/10 p-6">
                <span className="font-display text-4xl text-[#8EA694] leading-none">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-display text-2xl">{step.title}</h3>
                <p className="mt-2 text-[#EAE5D9]/85 leading-relaxed">{step.text}</p>
              </motion.div>
            ))}
          </div>
          <Link to="/kontakt?kilde=samarbejdsproces" onClick={cta("process")} className="group inline-flex items-center gap-2 mt-10 rounded-full bg-[#C47C5D] text-white px-7 py-3.5 font-medium hover:bg-[#b06a4c] transition-colors duration-300" data-testid="kommuner-process-cta">
            {k.processCta} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* DELIVERABLES */}
      <Section data-testid="kommuner-deliverables">
        <Reveal><div className="flex items-center gap-3 mb-3"><ClipboardList className="h-6 w-6 text-[#2D5A3F]" /><Eyebrow>{k.deliverablesIntro}</Eyebrow></div></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-3xl sm:text-4xl text-[#1A2E24] leading-tight max-w-3xl">{k.deliverablesTitle}</h2></Reveal>
        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          {k.deliverables.map((d) => (
            <div key={d} className="flex items-start gap-3 text-[#3B5245]"><Check className="h-5 w-5 text-[#2D5A3F] shrink-0 mt-0.5" /> {d}</div>
          ))}
        </div>
        <div className="mt-6 rounded-2xl bg-[#EAE5D9] p-6 border-l-4 border-[#C47C5D] text-[#3B5245] leading-relaxed">{k.deliverablesNote}</div>
      </Section>

      {/* PROGRESSION */}
      <section className="bg-[#F7F5F0] border-y border-black/5" data-testid="kommuner-progression">
        <Section className="!py-20 lg:!py-24">
          <Reveal><div className="flex items-center gap-3 mb-3"><FileText className="h-6 w-6 text-[#2D5A3F]" /><Eyebrow>{k.progressionTitle}</Eyebrow></div></Reveal>
          <Reveal delay={0.05}><p className="text-lg text-[#3B5245] leading-relaxed max-w-3xl">{k.progressionIntro}</p></Reveal>
          <div className="mt-7 flex flex-wrap gap-2.5">
            {k.progressionAreas.map((a) => (
              <span key={a} className="inline-flex rounded-full bg-white border border-[#D3CEC0] px-4 py-2 text-sm text-[#3B5245]">{a}</span>
            ))}
          </div>
          <div className="mt-7 rounded-2xl bg-white border border-black/5 p-6 text-[#3B5245] leading-relaxed">{k.progressionNote}</div>
        </Section>
      </section>

      {/* COLLABORATION */}
      <Section data-testid="kommuner-collab">
        <Reveal><div className="flex items-center gap-3 mb-3"><Users className="h-6 w-6 text-[#2D5A3F]" /><Eyebrow>{k.collabTitle}</Eyebrow></div></Reveal>
        <Reveal delay={0.05}><p className="text-lg text-[#3B5245] leading-relaxed max-w-3xl">{k.collabIntro}</p></Reveal>
        <div className="mt-7 flex flex-wrap gap-2.5">
          {k.collaborators.map((a) => (
            <span key={a} className="inline-flex rounded-full bg-[#EAE5D9] px-4 py-2 text-sm text-[#1A2E24]">{a}</span>
          ))}
        </div>
        <div className="mt-6 rounded-2xl bg-[#EAE5D9] p-6 border-l-4 border-[#8EA694] text-[#3B5245] leading-relaxed">{k.collabNote}</div>
      </Section>

      {/* BUSINESS */}
      <section className="bg-[#1A2E24] text-white" data-testid="kommuner-business">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <div className="flex items-center gap-3 mb-3"><Building2 className="h-6 w-6 text-[#8EA694]" /><Eyebrow dark>{k.businessTitle}</Eyebrow></div>
          <p className="text-lg text-[#EAE5D9] leading-relaxed max-w-3xl">{k.businessIntro}</p>
          <div className="mt-7 grid sm:grid-cols-2 gap-3">
            {k.business.map((b) => (
              <div key={b} className="flex items-start gap-3 text-[#EAE5D9]/90"><Check className="h-5 w-5 text-[#8EA694] shrink-0 mt-0.5" /> {b}</div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl bg-white/[0.05] border border-white/10 p-6 text-[#EAE5D9] leading-relaxed">{k.businessNote}</div>
        </div>
      </section>

      {/* FIVE PATHWAYS */}
      <Section data-testid="kommuner-forloeb">
        <Reveal><Eyebrow>{k.forloebTitle}</Eyebrow></Reveal>
        <Reveal delay={0.05}><p className="mt-4 text-lg text-[#3B5245] max-w-2xl">{k.forloebIntro}</p></Reveal>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <Link key={p.slug} to={`/indsatser/${p.slug}`} className="group rounded-2xl bg-white border border-black/5 p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid={`kommuner-forloeb-${p.slug}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl text-[#1A2E24]">{p.name}</h3>
                <ArrowUpRight className="h-5 w-5 text-[#8EA694]" />
              </div>
              <p className="mt-1 text-sm text-[#5C7567]">{p.duration}</p>
              <p className="mt-3 text-[#3B5245] leading-relaxed text-sm">{p.summary}</p>
            </Link>
          ))}
        </div>
      </Section>

      {/* GUARANTEES */}
      <section className="bg-[#EAE5D9]" data-testid="kommuner-guarantees">
        <Section className="!py-20 lg:!py-24">
          <Reveal><h2 className="font-display text-3xl sm:text-4xl text-[#1A2E24] mb-10">{k.guaranteesTitle}</h2></Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-3xl bg-white border border-black/5 p-7">
              <h3 className="font-display text-2xl text-[#2D5A3F] mb-5">{k.canTitle}</h3>
              <ul className="space-y-2.5">
                {k.can.map((c) => (<li key={c} className="flex items-start gap-3 text-[#1A2E24]"><Check className="h-5 w-5 text-[#2D5A3F] shrink-0 mt-0.5" /> {c}</li>))}
              </ul>
            </div>
            <div className="rounded-3xl bg-white border border-black/5 p-7">
              <h3 className="font-display text-2xl text-[#5C7567] mb-5">{k.cannotTitle}</h3>
              <ul className="space-y-2.5">
                {k.cannot.map((c) => (<li key={c} className="flex items-start gap-3 text-[#3B5245]"><X className="h-5 w-5 text-[#C47C5D] shrink-0 mt-0.5" /> {c}</li>))}
              </ul>
            </div>
          </div>
        </Section>
      </section>

      {/* FAQ */}
      <Section data-testid="kommuner-faq">
        <Reveal><Eyebrow>{k.faqTitle}</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <Accordion type="single" collapsible className="mt-8 space-y-3" onValueChange={(v) => v && trackEvent(EVENTS.COMMISSIONER_FAQ_OPEN, { faq_id: v, lang })}>
            {k.faq.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="rounded-2xl bg-white border border-black/5 px-5 sm:px-6 overflow-hidden" data-testid={`kommuner-faq-${i}`}>
                <AccordionTrigger className="hover:no-underline py-5 text-left font-display text-xl sm:text-2xl text-[#1A2E24]">{item.q}</AccordionTrigger>
                <AccordionContent className="pb-6 text-[#3B5245] leading-relaxed text-base">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </Section>

      {/* CONTACT */}
      <section className="bg-[#1A2E24] text-white" data-testid="kommuner-contact">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20 lg:py-28 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl mx-auto">{k.contactTitle}</h2>
          <p className="mt-5 text-lg text-[#EAE5D9] max-w-2xl mx-auto leading-relaxed">{k.contactSub}</p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/kontakt?kilde=kommune-side" onClick={() => { cta("contact-primary")(); trackEvent(EVENTS.COMMISSIONER_CONTACT_STARTED, { lang }); }} className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#F7F5F0] text-[#1A2E24] px-7 py-4 font-medium hover:bg-white transition-colors duration-300" data-testid="kommuner-contact-primary">
              {k.contactPrimary} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/find-forloeb" onClick={() => trackEvent(EVENTS.COMMISSIONER_FINDER_CLICK, { lang })} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white px-7 py-4 font-medium hover:bg-white/10 transition-colors duration-300" data-testid="kommuner-contact-finder">
              {k.contactFinder}
            </Link>
            <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center justify-center gap-2 rounded-full text-[#8EA694] px-5 py-4 font-medium hover:text-white transition-colors duration-300" data-testid="kommuner-contact-call">
              <Phone className="h-4 w-4" /> {CONTACT.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Kommuner;
